import axios from "axios";
import { AUTH_CLEARED_EVENT, isLoginEntry, requiresFreshLogin } from "./authSession";

// Authentication must remain on the same host as the loaded app so browser
// session cookies never cross the www/apex redirect boundary.
const API_BASE = "/api";

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach Authorization header from localStorage (mobile/cross-site cookie fallback)
api.interceptors.request.use((config) => {
  const token = !config.skipStoredAuth && !isLoginEntry() && !requiresFreshLogin()
    ? localStorage.getItem("access_token") : null;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

window.addEventListener(AUTH_CLEARED_EVENT, () => {
  delete api.defaults.headers.common.Authorization;
  delete axios.defaults.headers.common.Authorization;
});

export function shouldRedirectForSessionError(status, detail, isAuthRequest) {
  const normalized = String(detail || "").toLowerCase();
  const isPending = normalized.includes("awaiting") || normalized.includes("pending");
  return !isAuthRequest && (status === 401 || (status === 403 && isPending));
}

// Protected pages often load several resources in parallel. A pending or
// under-permissioned account must return to a clear access state instead of
// leaving an uncaught Axios rejection that triggers the runtime overlay.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = String(error?.response?.data?.detail || "").toLowerCase();
    const isAuthRequest = String(error?.config?.url || "").includes("/auth/");
    const isPending = detail.includes("awaiting") || detail.includes("pending");
    if (shouldRedirectForSessionError(status, detail, isAuthRequest) && typeof window !== "undefined") {
      window.location.replace(`/login?${isPending ? "pending=1" : "error=1"}`);
      // The navigation owns the outcome; keep parallel page loaders from
      // becoming unhandled rejections while the access screen loads.
      return new Promise(() => {});
    }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export { API_BASE };
